import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md shadow-sm"
          : "border-transparent bg-background-50/80 backdrop-blur-sm"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-6 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-clapperboard-fill text-lg"></i>
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950 md:text-xl">
            金桔<span className="text-primary-600">影院</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden items-center rounded-full border border-background-200 bg-background-100/70 px-3.5 py-1.5 md:flex md:w-64 lg:w-72"
        >
          <i className="ri-search-line text-base text-foreground-400"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索电影、电视剧"
            className="w-full bg-transparent px-2 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="菜单"
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-700 lg:hidden"
        >
          <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-4 flex items-center rounded-full border border-background-200 bg-background-100/70 px-3.5 py-2"
          >
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧"
              className="w-full bg-transparent px-2 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-3.5 py-2.5 text-sm font-semibold ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}